/** Copyright (c) 2026 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Box, Flex, Input, Tag, TagCloseButton, TagLabel, Text, VStack } from '@chakra-ui/react';
import { UserMultiTextSelectComponent } from '@karya/core';
import { ParsedParams } from '@karya/generic-task-react';
import { ChangeEventHandler, useMemo, useState } from 'react';
import { ColorMap } from '@/themes/Attributes';
import { ComponentRendererProps } from '../ComponentRenderer';

const DEFAULT_OPTIONS = ['Option A', 'Option B', 'Option C'];
const DEFAULT_ERROR = 'Invalid input';
const COMPONENT_WIDTH = '220px';

type InputMode = 'text' | 'numeric' | 'decimal';

// Keyboard type, same as the Android client
function getInputMode(type: string | null | undefined): InputMode {
  switch (type) {
    case 'INTEGER':
      return 'numeric';
    case 'FLOAT':
      return 'decimal';
    default:
      return 'text';
  }
}

/** Preview of the multi text select component: filter options by typing, pick many as chips. */
export function UserMultiTextSelectRenderer(props: ComponentRendererProps) {
  const typedParams = props.node.params as ParsedParams<UserMultiTextSelectComponent>;
  const component = props.node.component as UserMultiTextSelectComponent;

  const [query, setQuery] = useState('');

  const options = useMemo(
    () => (typedParams.options?.length ? typedParams.options : DEFAULT_OPTIONS),
    [typedParams.options],
  );
  const selected = useMemo(() => typedParams.selected ?? [], [typedParams.selected]);
  const errorMessage = typedParams.error || DEFAULT_ERROR;
  const inputMode = getInputMode(typedParams.type);

  // Prefix match, case insensitive
  const filteredOptions = useMemo(
    () => options.filter((opt) => opt.toLowerCase().startsWith(query.toLowerCase())),
    [options, query],
  );
  const availableOptions = filteredOptions.filter((opt) => !selected.includes(opt));

  const writeSelected = (value: string[]) => props.writeOutput(component.selected, value);

  const selectOption = (opt: string) => {
    if (!props.isCurrent || selected.includes(opt)) {
      return;
    }
    writeSelected([...selected, opt]);
    setQuery('');
  };

  const removeOption = (opt: string) => {
    if (!props.isCurrent) {
      return;
    }
    writeSelected(selected.filter((s) => s !== opt));
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!props.isCurrent) {
      return;
    }
    const value = e.target.value;
    // Full match selects the option
    const exactMatch = options.find((opt) => opt.toLowerCase() === value.toLowerCase());
    if (exactMatch && !selected.includes(exactMatch)) {
      selectOption(exactMatch);
      return;
    }
    setQuery(value);
  };

  const chips = (
    <Flex wrap="wrap" gap="4px" width="100%">
      {selected.map((opt) => (
        <Tag
          key={opt}
          size="sm"
          borderRadius="full"
          bg="rgba(0, 137, 87, 0.15)"
          borderWidth="1px"
          borderColor="#008857"
          maxWidth="100%"
        >
          <TagLabel whiteSpace="normal" wordBreak="break-word">
            {opt}
          </TagLabel>
          {props.isCurrent && <TagCloseButton aria-label={`Remove ${opt}`} onClick={() => removeOption(opt)} />}
        </Tag>
      ))}
    </Flex>
  );

  // Answered: read-only
  if (!props.isCurrent) {
    return (
      <Box width={COMPONENT_WIDTH} maxWidth="100%">
        {selected.length > 0 ? (
          chips
        ) : (
          <Text fontSize="sm" color={ColorMap.lightGray}>
            No option selected
          </Text>
        )}
      </Box>
    );
  }

  return (
    <VStack width={COMPONENT_WIDTH} maxWidth="100%" alignItems="stretch" spacing="6px">
      <Input
        size="sm"
        borderRadius="8px"
        placeholder="Type to search"
        inputMode={inputMode}
        value={query}
        onChange={handleChange}
        isInvalid={filteredOptions.length === 0}
      />
      {filteredOptions.length === 0 && (
        <Text fontSize="xs" color="red.500">
          {errorMessage}
        </Text>
      )}
      {selected.length > 0 && chips}
      {availableOptions.length > 0 && (
        <VStack
          alignItems="stretch"
          spacing={0}
          maxHeight="120px"
          overflowY="auto"
          borderWidth="1px"
          borderColor={ColorMap.lightGray}
          borderRadius="8px"
        >
          {availableOptions.map((opt) => (
            <Box
              key={opt}
              as="button"
              type="button"
              textAlign="start"
              px={2}
              py={1}
              fontSize="sm"
              whiteSpace="normal"
              wordBreak="break-word"
              _hover={{ bg: 'gray.100' }}
              onClick={() => selectOption(opt)}
            >
              {opt}
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
}
